import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, TextField, Button, IconButton, Modal,
    Typography, Paper, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle, Checkbox, FormControlLabel,
    MenuItem
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import Cookies from 'universal-cookie';
import {
    fetchAccountsFromAPI,
    createAccountInAPI,
    updateAccountInAPI,
    deleteAccountFromAPI
} from '../../Redux/AccountDucks';
import { fetchBusinessesFromAPI } from '../../Redux/BusinessDucks';

const getHeaders = () => {
    const cookies = new Cookies();
    return { headers: { Authorization: `Bearer ${cookies.get('token')}` } };
};

const AccountPage = () => {
    const dispatch = useDispatch();

    const accounts = useSelector(store => store.account.accounts);
    const businessLines = useSelector(store => store.business);
    const [tenants, setTenants] = useState([]);

    const [loading, setLoading] = useState(true);
    const [filterText, setFilterText] = useState('');
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [accountToDelete, setAccountToDelete] = useState(null);

    // Form state
    const [waba, setWaba] = useState('');
    const [number, setNumber] = useState('');
    const [identifier, setIdentifier] = useState('');
    const [active, setActive] = useState(true);
    const [tenantId, setTenantId] = useState('');
    const [businessLineId, setBusinessLineId] = useState('');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        dispatch(fetchAccountsFromAPI());
        dispatch(fetchBusinessesFromAPI());
        try {
            const response = await axios.get(window.API_URL + '/GetAllTenants', getHeaders());
            setTenants(response.data);
        } catch (error) {
            console.error('Error fetching tenants:', error);
        }
        setLoading(false);
    };

    const resetForm = () => {
        setWaba('');
        setNumber('');
        setIdentifier('');
        setActive(true);
        setTenantId('');
        setBusinessLineId('');
    };

    const handleAdd = () => {
        setSelectedAccount(null);
        resetForm();
        setIsModalOpen(true);
    };

    const handleEdit = (account) => {
        setSelectedAccount(account);
        setWaba(account.waba || '');
        setNumber(account.number || '');
        setIdentifier(account.identifier || '');
        setActive(account.active);
        setTenantId(account.tenantId || '');
        setBusinessLineId(account.businessLineId || '');
        setIsModalOpen(true);
    };

    const handleDelete = (account) => {
        setAccountToDelete(account);
        setIsDeleteDialogOpen(true);
    };

    const handleSave = async () => {
        const dto = {
            waba,
            number: number || null,
            identifier: identifier || null,
            active,
            tenantId: tenantId !== '' ? parseInt(tenantId) : null,
            businessLineId: businessLineId !== '' ? parseInt(businessLineId) : null,
        };

        if (selectedAccount) {
            await dispatch(updateAccountInAPI({ ...dto, id: selectedAccount.id }));
        } else {
            await dispatch(createAccountInAPI(dto));
        }
        setIsModalOpen(false);
        loadData();
    };

    const confirmDelete = () => {
        dispatch(deleteAccountFromAPI(accountToDelete.id));
        setIsDeleteDialogOpen(false);
    };

    const getStatusText = (active) => active ? 'Activo' : 'Inactivo';

    const columns = [
        {
            name: 'Número',
            selector: row => row.number,
            sortable: true,
        },
        {
            name: 'Waba',
            selector: row => row.waba,
            sortable: true,
            grow: 2,
        },
        {
            name: 'Identifier',
            selector: row => row.identifier,
            sortable: true,
        },
        {
            name: 'Línea de Negocio',
            selector: row => row.businessLineName,
            sortable: true,
        },
        {
            name: 'Tenant',
            selector: row => row.tenantName || '-',
            sortable: true,
        },
        {
            name: 'Estado',
            selector: row => getStatusText(row.active),
            sortable: true,
            cell: row => (
                <span className={`apple-badge ${row.active ? 'apple-badge-success' : 'apple-badge-error'}`}>
                    {getStatusText(row.active)}
                </span>
            ),
        },
        {
            name: 'Acciones',
            cell: row => (
                <Box>
                    <IconButton onClick={() => handleEdit(row)} size="small" className="apple-button apple-tooltip">
                        <EditIcon fontSize="small" />
                        <span className="tooltiptext">Editar</span>
                    </IconButton>
                    <IconButton onClick={() => handleDelete(row)} size="small" className="apple-button apple-tooltip">
                        <RemoveCircleOutlineIcon fontSize="small" color="error" />
                        <span className="tooltiptext">Desactivar</span>
                    </IconButton>
                </Box>
            ),
            width: '160px',
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#F2F2F7',
                borderBottom: 'none',
            },
        },
        headCells: {
            style: {
                fontSize: '12px',
                fontWeight: '600',
                color: '#86868B',
                textTransform: 'uppercase',
            },
        },
        cells: {
            style: {
                fontSize: '14px',
                color: '#1D1D1F',
            },
        },
    };

    const filteredItems = (Array.isArray(accounts) ? accounts : []).filter(
        item =>
            (item.number && item.number.toLowerCase().includes(filterText.toLowerCase())) ||
            (item.waba && item.waba.toLowerCase().includes(filterText.toLowerCase()))
    );

    const subHeaderComponent = useMemo(() => (
        <Box className="apple-card" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 2 }}>
            <div className="apple-search-container">
                <SearchIcon className="apple-search-icon" />
                <input
                    type="text"
                    placeholder="Buscar por número o WABA"
                    onChange={e => setFilterText(e.target.value)}
                    className="apple-search-input"
                />
            </div>
            <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAdd}
                className="apple-button"
            >
                Agregar Cuenta WSP
            </Button>
        </Box>
    ), [filterText]);

    return (
        <div className="apple-container">
            <Paper className="apple-card">
                <DataTable
                    columns={columns}
                    data={filteredItems}
                    pagination
                    paginationPerPage={10}
                    paginationRowsPerPageOptions={[10, 20, 30]}
                    subHeader
                    subHeaderComponent={subHeaderComponent}
                    progressPending={loading}
                    persistTableHead
                    customStyles={customStyles}
                />
            </Paper>

            {/* Modal Crear / Editar */}
            <Modal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
            >
                <Box className="apple-modal" sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 600,
                }}>
                    <Typography className="apple-modal-title">
                        {selectedAccount ? 'Editar Cuenta WhatsApp' : 'Agregar Cuenta WhatsApp'}
                    </Typography>
                    <div className="apple-modal-content">
                        <TextField
                            label="Waba *"
                            variant="outlined"
                            fullWidth
                            className="apple-input"
                            value={waba}
                            onChange={e => setWaba(e.target.value)}
                        />
                        <TextField
                            label="Número"
                            variant="outlined"
                            fullWidth
                            className="apple-input"
                            value={number}
                            onChange={e => setNumber(e.target.value)}
                        />
                        <TextField
                            label="Identifier"
                            variant="outlined"
                            fullWidth
                            className="apple-input"
                            value={identifier}
                            onChange={e => setIdentifier(e.target.value)}
                        />
                        <TextField
                            select
                            label="Línea de Negocio"
                            fullWidth
                            className="apple-input"
                            value={businessLineId}
                            onChange={e => setBusinessLineId(e.target.value)}
                        >
                            <MenuItem value="">Sin línea de negocio</MenuItem>
                            {(Array.isArray(businessLines) ? businessLines : []).map(b => (
                                <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label="Tenant"
                            fullWidth
                            className="apple-input"
                            value={tenantId}
                            onChange={e => setTenantId(e.target.value)}
                        >
                            <MenuItem value="">Sin tenant</MenuItem>
                            {tenants.map(t => (
                                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                            ))}
                        </TextField>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={active}
                                    onChange={e => setActive(e.target.checked)}
                                />
                            }
                            label="Activo"
                        />
                    </div>
                    <div className="apple-modal-footer">
                        <Button onClick={() => setIsModalOpen(false)} className="apple-button">
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            className="apple-button"
                            onClick={handleSave}
                            disabled={!waba}
                        >
                            {selectedAccount ? 'Guardar Cambios' : 'Crear Cuenta'}
                        </Button>
                    </div>
                </Box>
            </Modal>

            {/* Diálogo de confirmación desactivar */}
            <Dialog
                open={isDeleteDialogOpen}
                onClose={() => setIsDeleteDialogOpen(false)}
            >
                <DialogTitle>Confirmar desactivación</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Está seguro de que desea desactivar la cuenta <strong>{accountToDelete?.waba}</strong>?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDeleteDialogOpen(false)} className="apple-button">
                        Cancelar
                    </Button>
                    <Button onClick={confirmDelete} className="apple-button" autoFocus>
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default AccountPage;
